import { useCallback } from 'react';
export function usePreorderCheckout() {
    const startPayment = useCallback(async (p) => {
        const { apiBase, apiHeaders, campaignId, items, pickupDate, deliveryMethod, customerName, customerPhone, customerNote, lineId, channel, pickupType, invoice, shippingAddress, convenienceStoreInfo, paymentProvider, ecpayChoosePayment, onUserNotRegistered, pickupPaymentMode, } = p;
        // 1) order-for-payment（預購預設 channel=online_shopping，不需內用/外帶）
        const orderRes = await fetch(`${apiBase}/api/preorders/order-for-payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...apiHeaders },
            body: JSON.stringify({
                campaignId,
                items,
                pickupDate,
                deliveryMethod,
                channel: channel ?? 'online_shopping',
                ...(channel === 'online_order' && deliveryMethod === 'store_pickup'
                    ? { pickupType: pickupType ?? 'takeout' }
                    : {}),
                customerName,
                customerPhone,
                customerNote,
                lineId,
                invoiceType: invoice?.type,
                taxId: invoice?.taxId,
                invoiceTitle: invoice?.invoiceTitle,
                carrierType: invoice?.carrierType,
                carrierNum: invoice?.carrierNum,
                loveCode: invoice?.loveCode,
                email: invoice?.email,
                ...(shippingAddress ? { shippingAddress } : {}),
                ...(convenienceStoreInfo ? { convenienceStoreInfo } : {}),
                ...(paymentProvider === 'cvs_cod'
                    ? { paymentType: 'cvs_cod' }
                    : {}),
            }),
        });
        if (!orderRes.ok) {
            const err = await orderRes.json().catch(() => ({}));
            if (err?.code === 'USER_NOT_REGISTERED' ||
                String(err?.message || '').includes('尚未註冊為會員')) {
                onUserNotRegistered?.();
                throw new Error('USER_NOT_REGISTERED');
            }
            // 400 驗證錯誤：優先顯示後端回傳的 message 或 details 中第一個欄位錯誤
            const details = err?.details;
            const firstValidation = details?.validationErrors?.[0];
            const message = err?.message ||
                (firstValidation
                    ? `${firstValidation.field ? `${firstValidation.field}: ` : ''}${firstValidation.message ?? ''}`
                    : null) ||
                details?.summary ||
                `order-for-payment failed: ${orderRes.status}`;
            throw new Error(message);
        }
        const orderData = await orderRes.json().catch(() => ({}));
        // 後端回傳 { success: true, data: { orderId, orderNumber }, timestamp }
        const data = orderData?.data;
        const orderId = (data && (data.orderId ?? data.order_id)) ??
            orderData?.orderId ??
            orderData.order_id;
        const orderNumber = (data &&
            (data.orderNumber ??
                data.order_number)) ??
            orderData?.orderNumber ??
            orderData?.order_num ??
            orderData.order_number;
        if (!orderId) {
            throw new Error('order-for-payment: missing orderId (response: ' +
                JSON.stringify(orderData).slice(0, 200) +
                ')');
        }
        // 超商貨到付款／面交付款：不呼叫 payment/request，直接回傳訂單成立
        if (paymentProvider === 'cvs_cod' || pickupPaymentMode) {
            return {
                type: 'ok',
                data: orderData,
                orderId,
                orderNumber,
            };
        }
        // 2) payment/request
        const paymentRes = await fetch(`${apiBase}/api/preorders/payment/request`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...apiHeaders },
            body: JSON.stringify({
                orderId,
                paymentProvider,
                ...(paymentProvider === 'ecpay' && ecpayChoosePayment
                    ? { ecpayChoosePayment }
                    : {}),
            }),
        });
        if (!paymentRes.ok) {
            const err = await paymentRes.json().catch(() => ({}));
            if (err?.code === 'USER_NOT_REGISTERED' ||
                String(err?.message || '').includes('尚未註冊為會員')) {
                onUserNotRegistered?.();
                throw new Error('USER_NOT_REGISTERED');
            }
            throw new Error(err?.message || `payment/request failed: ${paymentRes.status}`);
        }
        const paymentData = await paymentRes.json().catch(() => ({}));
        const payData = paymentData?.data ?? paymentData?.result ?? {};
        // 支援 camelCase / snake_case（後端或代理可能轉換）
        const formActionUrl = payData.formAction ?? payData.paymentUrl ?? payData.payment_url;
        const formDataPayload = payData.formData ?? payData.form_data ?? payData.FormData;
        const redirectUrlPayload = payData.redirectUrl ?? payData.redirect_url;
        // ECPay：必須有 paymentUrl + formData，以 POST form 導向綠界
        if (formActionUrl &&
            formDataPayload &&
            Object.keys(formDataPayload).length > 0) {
            return {
                type: 'form',
                formAction: formActionUrl,
                formData: typeof formDataPayload === 'object' && formDataPayload !== null
                    ? formDataPayload
                    : {},
            };
        }
        // LINE Pay 等：直接 redirect URL（後端可能只回傳 paymentUrl，未回傳 redirectUrl）
        if (redirectUrlPayload) {
            return { type: 'redirectUrl', url: redirectUrlPayload };
        }
        if (formActionUrl && paymentProvider === 'ecpay') {
            throw new Error('綠界未回傳付款表單，請確認商家已啟用綠界金流或稍後再試');
        }
        // 僅有 paymentUrl、無 formData（例如 LINE Pay）：視為直接導向
        if (formActionUrl) {
            return { type: 'redirectUrl', url: formActionUrl };
        }
        // 回傳 ok 時，將 orderId / orderNumber 也放上層，方便前端統一導向處理
        return {
            type: 'ok',
            data: paymentData,
            orderId,
            orderNumber,
        };
    }, []);
    async function confirmPaymentFromQuery(p) {
        const { apiBase, getApiHeaders, transactionId, orderId, contextTenantId } = p;
        if (!transactionId && !orderId) {
            throw new Error('missing transactionId and orderId');
        }
        // If only orderId (ECPay), query by-order first
        if (!transactionId && orderId) {
            const byOrderRes = await fetch(`${apiBase}/api/preorders/payment/info/by-order?orderId=${encodeURIComponent(orderId)}`, {
                headers: getApiHeaders(contextTenantId || undefined),
            });
            if (!byOrderRes.ok) {
                const err = await byOrderRes.json().catch(() => ({}));
                throw new Error(err?.message || `by-order failed: ${byOrderRes.status}`);
            }
            const byOrder = await byOrderRes.json();
            if (!byOrder?.success || !byOrder?.data) {
                throw new Error('by-order: invalid response');
            }
            const d = byOrder.data;
            if (d.paymentStatus === '已付款' || d.status === 'paid') {
                return { status: 'success', orderNumber: d.orderNumber };
            }
            // 超商貨到付款／面交付款：訂單已成立，款項於取貨時支付，視為成功
            const paymentMethod = d.payment_method;
            if (paymentMethod === 'cvs_cod') {
                return {
                    status: 'success',
                    orderNumber: d.orderNumber,
                    isCodSuccess: true,
                };
            }
            if (paymentMethod === 'pickup_pay') {
                return {
                    status: 'success',
                    orderNumber: d.orderNumber,
                    isPickupPaySuccess: true,
                };
            }
            return { status: 'pending', message: '付款尚未完成' };
        }
        // For LINE Pay (transactionId exists)
        if (!transactionId)
            throw new Error('missing transactionId');
        // query info by transaction
        const infoRes = await fetch(`${apiBase}/api/preorders/payment/info/by-transaction?transactionId=${encodeURIComponent(transactionId)}`, {
            headers: getApiHeaders(contextTenantId || undefined),
        });
        if (!infoRes.ok) {
            const text = await infoRes.text().catch(() => '');
            throw new Error(text || `info/by-transaction failed: ${infoRes.status}`);
        }
        const infoData = await infoRes.json();
        if (!infoData?.success || !infoData?.data)
            throw new Error('invalid info response');
        const paymentInfo = infoData.data;
        const tenantId = paymentInfo.tenantId;
        const finalOrderId = orderId || paymentInfo.orderId;
        if (!tenantId)
            throw new Error('missing tenantId in payment info');
        if (!finalOrderId)
            throw new Error('missing orderId');
        // confirm
        const apiHeaders = getApiHeaders(tenantId);
        const confirmRes = await fetch(`${apiBase}/api/preorders/payment/confirm?transactionId=${encodeURIComponent(transactionId)}&orderId=${encodeURIComponent(finalOrderId)}`, {
            method: 'POST',
            headers: apiHeaders,
            body: JSON.stringify({}),
        });
        if (!confirmRes.ok) {
            const err = await confirmRes.json().catch(() => ({}));
            throw new Error(err?.message || `confirm failed: ${confirmRes.status}`);
        }
        const confirmData = await confirmRes.json();
        if (!confirmData?.success || !confirmData?.data)
            throw new Error('confirm: invalid response');
        return {
            status: 'success',
            orderNumber: confirmData.data.orderNumber,
            tenantId: confirmData.data.tenantId,
        };
    }
    return { startPayment, confirmPaymentFromQuery };
}
export default usePreorderCheckout;
